"use client";

import React from "react";
import dynamic from "next/dynamic";
import type { DynamicFlowBaseProps } from "@highereducation/supersonic-ui";
import { SearchFypPlacement } from "@/monarch/rules/searchFypEntry/types";
import useTranslations from "@/i18n/useTranslations";

const SEARCH_FYP_SLOT_PLACEMENT: NonNullable<
  DynamicFlowBaseProps["placement"]
> = "belowTheFold";

function SearchFypLoading() {
  const t = useTranslations("Common");
  return (
    <div
      className="min-h-[120px] animate-pulse rounded-xl bg-gray-200"
      data-testid="search-fyp-skeleton"
      role="status"
      aria-label={t("loadingLearningPathFinder")}
    />
  );
}

const DynamicFlowWrapper = dynamic(
  () => import("@/components/DynamicFlowWrapper"),
  {
    ssr: false,
    loading: () => <SearchFypLoading />,
  },
);

interface SearchFypEntryProps {
  placement: SearchFypPlacement;
  forcedFlowId?: string;
  jumpToStepId?: string;
  styleVariant?: "default" | "alt";
  isLoading?: boolean;
}

export default function SearchFypEntry({
  placement,
  forcedFlowId,
  jumpToStepId,
  styleVariant,
  isLoading = false,
}: SearchFypEntryProps) {
  const t = useTranslations("Common");
  if (isLoading) {
    return <SearchFypLoading />;
  }

  return (
    <div
      role="region"
      aria-labelledby={`search-fyp-heading-${placement}`}
      data-testid="search-fyp-entry"
    >
      <h2 id={`search-fyp-heading-${placement}`} className="sr-only">
        {t("findYourLearningPath")}
      </h2>
      <DynamicFlowWrapper
        placement={SEARCH_FYP_SLOT_PLACEMENT}
        forcedFlowId={forcedFlowId}
        jumpToStepId={jumpToStepId}
        styleVariant={styleVariant}
      />
    </div>
  );
}
